import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { Modal, FormField } from '../../ui/Modal';

interface LocationFormState {
  city: string;
}

const EMPTY: LocationFormState = {
  city: '',
};

export function LocationForm({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing?: any | null;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<LocationFormState>(EMPTY);

  useEffect(() => {
    if (editing) {
      setForm({ city: editing.city ?? '' });
    } else {
      setForm(EMPTY);
    }
  }, [editing, open]);

  const save = useMutation({
    mutationFn: () => {
      const body = { name: form.city, city: form.city };
      return editing
        ? api.patch(`/locations/${editing.id}`, body).then((r) => r.data)
        : api.post('/locations', body).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations'] });
      toast.success(editing ? 'Location updated' : 'Location created');
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit location' : 'New location'}
      subtitle="Clinic locations where services are delivered."
      size="sm"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!form.city || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create location'}
          </button>
        </>
      }
    >
      <FormField label="Name" required>
        <input
          className="input"
          autoFocus
          value={form.city}
          onChange={(e) => setForm({ city: e.target.value })}
          placeholder="e.g. Detroit Clinic"
        />
      </FormField>
    </Modal>
  );
}
